import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertCircle, Calendar, User } from 'lucide-react';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import TopNav from '../components/sections/TopNav';
import ShareButtons from '../components/ShareButtons';
import ArticleBadge from '../components/ArticleBadge';
import ArticleListenBar from '../components/ArticleListenBar';
import ArticleEngagement from '../components/ArticleEngagement';
import LiveReaderPill from '../components/LiveReaderPill';
import RelatedStories from '../components/RelatedStories';
import AdSlot from '../components/AdSlot';
import PaywallCard from '../components/PaywallCard';
import ArticleSignupNudge from '../components/ArticleSignupNudge';
import ContentProtection from '../components/ContentProtection';
import MeterChip from '../components/MeterChip';
import PhotoCredit from '../components/PhotoCredit';
import { useAuth } from '../context/AuthContext';
import { useJsonLd, useSeoMeta, useNoIndex, buildNewsArticle, buildBreadcrumb } from '../utils/jsonLd';
import { isExclusiveArticle, EXCLUSIVE_LEGAL_NOTICE_HTML, EXCLUSIVE_NOTICE_CSS } from '../utils/exclusive';

const API = process.env.REACT_APP_BACKEND_URL;

const Article = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const { user, clientId, refresh: refreshAuth } = useAuth();

  useEffect(() => {
    setLoading(true);
    setError('');
    setNotFound(false);
    fetch(`${API}/api/articles/${slug}?client_id=${encodeURIComponent(clientId || '')}`, {
      cache: 'no-store',
      credentials: 'include',
    })
      .then((r) => {
        if (r.status === 404) {
          setNotFound(true);
          throw new Error('Article not found.');
        }
        if (!r.ok) throw new Error('Could not load article.');
        return r.json();
      })
      .then((data) => {
        setArticle(data);
        // Refresh meter state after each article load so the header chip stays honest.
        refreshAuth();
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    // Fire an anonymous view event (dedupe + rate-limit handled server-side)
    fetch(`${API}/api/articles/${slug}/view`, { method: 'POST' }).catch(() => {});
  }, [slug, clientId, refreshAuth]);

  const dateLabel = article?.published_at
    ? (() => {
        const d = new Date(article.published_at);
        if (Number.isNaN(d.getTime())) return '';
        const datePart = d.toLocaleDateString('en-AU', {
          day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Australia/Melbourne',
        });
        const timePart = d.toLocaleTimeString('en-AU', {
          hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Australia/Melbourne',
        });
        return `${datePart} · ${timePart.toLowerCase()}`;
      })()
    : '';

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const canonicalUrl = article ? `https://www.outlawmedia.com.au/stories/${article.slug}` : '';

  // Imported stories carry their lead photo as the first image block inside  // the body. Lift it out so it can run edge-to-edge as a hero on desktop,
  // and hand the rest of the story to the normal reading column.
  const { heroHtml, bodyHtml } = useMemo(() => {
    const html = article?.body_html || '';
    // Locked stories: the server sends the lead photo separately so the
    // paywalled page still shows a hero.
    if (!html) return { heroHtml: article?.hero_html || '', bodyHtml: '' };
    if (typeof window === 'undefined') return { heroHtml: '', bodyHtml: html };
    try {
      const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
      const root = doc.getElementById('root');
      const candidates = Array.from(root.children).slice(0, 2);
      const hero = candidates.find(
        (el) => el.querySelector('img') && !el.matches('p'),
      );
      if (!hero) return { heroHtml: '', bodyHtml: html };
      const out = hero.outerHTML;
      hero.remove();
      return { heroHtml: out, bodyHtml: root.innerHTML };
    } catch {
      return { heroHtml: '', bodyHtml: html };
    }
  }, [article?.body_html, article?.hero_html]);

  const hasHero = Boolean(
    heroHtml || (article?.cover_image_url && article?.source !== 'imported'),
  );

  useSeoMeta(
    article
      ? {
          title: `${article.title} - Outlaw Media`,
          description: article.excerpt || article.subtitle || `${article.title} - Outlaw Media investigation.`,
          canonical: canonicalUrl,
          image: article.cover_image_url,
          type: 'article',
          publishedAt: article.published_at,
          modifiedAt: article.updated_at,
          author: article.author || 'Staff Writers',
          tags: article.tags || [],
        }
      : {},
  );

  useJsonLd(article ? buildNewsArticle(article) : null, 'article-newsarticle');
  useJsonLd(
    article
      ? buildBreadcrumb([
          { name: 'Home', url: 'https://www.outlawmedia.com.au/' },
          { name: 'Investigations', url: 'https://www.outlawmedia.com.au/stories' },
          { name: article.title, url: canonicalUrl },
        ])
      : null,
    'article-breadcrumb',
  );

  // Tell Google not to index this URL when the requested slug doesn't exist.
  // Prevents Google Search Console "Soft 404" flags on dead / renamed slugs.
  useNoIndex(notFound);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="article-page">
      <TopNav />
      <header className="bg-zinc-900/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-30">
        <div className="max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/stories" className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-bold" data-testid="article-back-stories">
            <ArrowLeft className="h-4 w-4" />
            All Investigations
          </Link>
          <span className="text-sm font-black tracking-widest text-red-400">STORY</span>
        </div>
      </header>

      <main className="max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        {loading && (
          <div className="text-center py-24" data-testid="article-loading">
            <RefreshCw className="h-8 w-8 text-red-500 animate-spin mx-auto mb-3" />
            <p className="text-gray-400">Loading story…</p>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-24" data-testid="article-error">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
            <p className="text-red-400 mb-4">{error}</p>
            <Link to="/stories" className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-white font-bold text-sm">
              Back to Investigations
            </Link>
          </div>
        )}

        {!loading && article && (
          <article>
            {/* Front-page hero: the lead photo runs edge-to-edge on desktop
                with the kicker, headline and standfirst laid over the bottom
                of it. On mobile the same markup stacks - photo, then text. */}
            <section
              className="relative mb-8 lg:mb-10 lg:w-screen lg:max-w-none lg:mx-[calc(50%-50vw)]"
              data-testid="article-hero-section"
            >
              {(article.cover_image_url && article.source !== 'imported') ? (
                <div className="relative" data-testid="article-cover">
                  <img
                    src={article.cover_image_url}
                    alt={article.title}
                    className="w-full rounded-xl border border-zinc-800 lg:rounded-none lg:border-x-0 lg:min-h-[560px] lg:max-h-[78vh] lg:object-cover"
                  />
                  <PhotoCredit label={article.photo_credit} />
                </div>
              ) : heroHtml ? (
                <div className="relative">
                  <div
                    className="article-hero"
                    data-testid="article-cover"
                    dangerouslySetInnerHTML={{ __html: heroHtml }}
                  />
                  <PhotoCredit label={article.photo_credit} />
                </div>
              ) : null}

              <div
                className={
                  hasHero
                    ? 'pt-5 lg:pt-0 lg:absolute lg:inset-x-0 lg:bottom-0 lg:bg-gradient-to-t lg:from-black lg:via-black/85 lg:to-transparent'
                    : 'pt-2'
                }
              >
                <div className={hasHero ? 'lg:max-w-5xl xl:max-w-6xl lg:mx-auto lg:px-10 lg:pt-36 lg:pb-9' : ''}>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <ArticleBadge article={article} size="lg" testId="article-hero-badge" />
                    {(() => {
                      // Hide the tag that's already shown as the kicker badge.
                      const badgeTags = ['exclusive', 'breaking', 'catch up', 'catchup', 'catch-up', 'opinion'];
                      const shown = (article.tags || []).filter((t) => !badgeTags.includes(String(t).toLowerCase().trim()));
                      if (shown.length === 0) return null;
                      return (
                        <span className="flex flex-wrap items-center gap-2" data-testid="article-tags">
                          {shown.map((t, i) => (
                            <React.Fragment key={t}>
                              {i > 0 && <span className="text-gray-600 text-[10px]">·</span>}
                              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                {t}
                              </span>
                            </React.Fragment>
                          ))}
                        </span>
                      );
                    })()}
                  </div>

                  <h1
                    className="text-3xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-3 drop-shadow-[0_2px_18px_rgba(0,0,0,0.9)]"
                    data-testid="article-title"
                  >
                    {article.title}
                  </h1>

                  {article.subtitle && (
                    <p
                      className="text-lg md:text-xl text-gray-300 leading-snug max-w-3xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]"
                      data-testid="article-subtitle"
                    >
                      {article.subtitle}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <div className="flex items-center flex-wrap gap-4 text-xs uppercase tracking-widest text-gray-500 border-y border-zinc-800 py-3 mb-8">
              <span className="flex items-center gap-1.5" data-testid="article-byline">
                <User className="h-3.5 w-3.5 text-red-400" /> {article.author || 'Staff Writers'}
              </span>
              {dateLabel && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-red-400" /> {dateLabel}
                </span>
              )}
              {article.meter && !article.body_locked && <MeterChip meter={article.meter} />}
              <div className="ml-auto">
                <ShareButtons
                  url={shareUrl}
                  title={article.title}
                  text={article.excerpt || article.subtitle || ''}
                  testId="article-share"
                />
              </div>
            </div>

            <ArticleListenBar slug={slug} bodyLocked={article.body_locked} />

            {/* Article body - split into two halves with a mid-story ad
                slot for readers who scroll past the fold. Split targets the
                paragraph closest to the middle so the break lands between
                naturally-flowing sections, not mid-sentence. If the body is
                paywall-locked the server has already trimmed it to the
                teaser - we render the PaywallCard instead of the ad break.
                The whole body is wrapped in <ContentProtection> which
                disables copy/right-click/drag-save. */}
            <ContentProtection>
            {article.body_locked ? (
              <>
                {article.lead_in_html && (
                  <div
                    className="article-body prose prose-invert max-w-none mb-6"
                    data-testid="article-lead-in"
                    dangerouslySetInnerHTML={{ __html: article.lead_in_html }}
                  />
                )}
                <div className="paywalled-body">
                  <PaywallCard grace={article.grace} meter={article.meter} />
                </div>
              </>
            ) : (() => {
              const html = bodyHtml || '';
              const paras = html.split(/(<\/p>)/i);
              // Rejoin paragraphs - every </p> stays glued to its preceding chunk
              const chunks = [];
              for (let i = 0; i < paras.length; i += 2) {
                chunks.push((paras[i] || '') + (paras[i + 1] || ''));
              }
              // Anonymous readers during the free-access grace get a hard
              // signup nudge after the first ~2 paragraphs, with the rest
              // of the story blurred behind it. Logged-in readers see the
              // article as normal.
              if (!user && article.grace?.active && chunks.length >= 3) {
                const cut = Math.min(2, Math.max(1, Math.floor(chunks.length / 4)));
                const opening = chunks.slice(0, cut).join('');
                const rest = chunks.slice(cut).join('');
                return (
                  <>
                    <div
                      className="prose prose-invert prose-lg lg:prose-xl article-prose max-w-none prose-headings:text-white prose-headings:font-black prose-a:text-red-400 hover:prose-a:text-red-300 prose-strong:text-white prose-blockquote:border-l-red-500 prose-blockquote:text-gray-300 prose-img:rounded-lg prose-img:border prose-img:border-zinc-800"
                      dangerouslySetInnerHTML={{ __html: opening }}
                      data-testid="article-body"
                    />
                    <ArticleSignupNudge grace={article.grace} />
                    <div className="relative" data-testid="article-body-blurred-wrap">
                      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black via-black/80 to-transparent z-10" />
                      <div
                        aria-hidden="true"
                        className="prose prose-invert prose-lg lg:prose-xl article-prose max-w-none prose-headings:text-white prose-headings:font-black prose-a:text-red-400 hover:prose-a:text-red-300 prose-strong:text-white prose-blockquote:border-l-red-500 prose-blockquote:text-gray-300 prose-img:rounded-lg prose-img:border prose-img:border-zinc-800 select-none pointer-events-none"
                        style={{ filter: 'blur(6px)', opacity: 0.45 }}
                        dangerouslySetInnerHTML={{ __html: rest }}
                        data-testid="article-body-blurred"
                      />
                      <div className="absolute inset-x-0 bottom-4 flex justify-center z-20 pointer-events-none">
                        <Link
                          to="/login"
                          data-testid="article-body-blurred-cta"
                          className="pointer-events-auto inline-flex items-center gap-2 px-5 py-3 rounded bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.25em] shadow-[0_0_30px_rgba(220,38,38,0.7)]"
                        >
                          Create a free account to read on
                        </Link>
                      </div>
                    </div>
                  </>
                );
              }
              // Only inject an ad if the article has 4+ paragraphs - short
              // pieces don't need one and it would feel intrusive.
              if (chunks.length < 4) {
                return (
                  <div
                    className="prose prose-invert prose-lg lg:prose-xl article-prose max-w-none prose-headings:text-white prose-headings:font-black prose-a:text-red-400 hover:prose-a:text-red-300 prose-strong:text-white prose-blockquote:border-l-red-500 prose-blockquote:text-gray-300 prose-img:rounded-lg prose-img:border prose-img:border-zinc-800"
                    dangerouslySetInnerHTML={{ __html: html }}
                    data-testid="article-body"
                  />
                );
              }
              const mid = Math.floor(chunks.length / 2);
              const firstHalf = chunks.slice(0, mid).join('');
              const secondHalf = chunks.slice(mid).join('');
              return (
                <>
                  <div
                    className="prose prose-invert prose-lg lg:prose-xl article-prose max-w-none prose-headings:text-white prose-headings:font-black prose-a:text-red-400 hover:prose-a:text-red-300 prose-strong:text-white prose-blockquote:border-l-red-500 prose-blockquote:text-gray-300 prose-img:rounded-lg prose-img:border prose-img:border-zinc-800"
                    dangerouslySetInnerHTML={{ __html: firstHalf }}
                    data-testid="article-body"
                  />
                  <AdSlot
                    slotId="article-mid"
                    className="my-8 px-0"
                    aspect="aspect-[4/1] md:aspect-[6/1]"
                  />
                  <div
                    className="prose prose-invert prose-lg lg:prose-xl article-prose max-w-none prose-headings:text-white prose-headings:font-black prose-a:text-red-400 hover:prose-a:text-red-300 prose-strong:text-white prose-blockquote:border-l-red-500 prose-blockquote:text-gray-300 prose-img:rounded-lg prose-img:border prose-img:border-zinc-800"
                    dangerouslySetInnerHTML={{ __html: secondHalf }}
                    data-testid="article-body-cont"
                  />
                </>
              );
            })()}
            </ContentProtection>
            <style>{`
              /* Mobile reading size - prose-lg (18px) read oversized next to
                 mainstream news apps, so drop to 17px/1.62 on phones. */
              @media (max-width: 640px) {
                .article-prose.prose-lg {
                  font-size: 1.0625rem;
                  line-height: 1.62;
                }
              }
              /* Normalise every image inside the story body so they all
                 render at the same width as the cover image - solves the
                 "different-sized images" complaint in email blasts + site. */
              .article-prose img,
              .article-prose picture img,
              .article-prose figure img,
              .article-prose .captioned-image-container img {
                display: block;
                width: 100%;
                max-width: 100%;
                height: auto;
                margin: 1.5rem auto;
                border-radius: 0.75rem;
                border: 1px solid rgb(39,39,42);
                object-fit: cover;
              }
              /* Image captions - imported <figure><figcaption> AND native
                 CaptionedImage nodes from the editor. Make them look like
                 captions, not part of the story body. */
              .article-prose figure { margin: 1.75rem 0 2rem; }
              .article-prose figure img,
              .article-prose figure picture img {
                margin: 0 auto;
              }
              .article-prose figcaption,
              .article-prose .image-caption {
                display: block;
                margin: 0.75rem auto 0;
                padding: 0.35rem 0.85rem 0;
                max-width: 90%;
                text-align: center;
                font-size: 0.82rem;
                line-height: 1.45;
                color: #a1a1aa;
                font-style: italic;
                font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
                border-top: 1px solid rgba(220,38,38,0.35);
              }
              .article-prose figcaption strong,
              .article-prose figcaption b { color: #fca5a5; font-style: normal; }
              .article-prose .captioned-image-container {
                margin: 1.75rem 0 2rem;
              }
              .article-prose .image2-inset,
              .article-prose .image2-inset picture,
              .article-prose .image2-inset img {
                display: block;
                width: 100%;
              }
              /* Legacy imported bodies wrap images in nested divs - flatten
                 the visual spacing */
              .article-prose .captioned-image-container > figure { margin: 0; }
            `}</style>

            {isExclusiveArticle(article) && (
              <>
                <style>{EXCLUSIVE_NOTICE_CSS}</style>
                <div dangerouslySetInnerHTML={{ __html: EXCLUSIVE_LEGAL_NOTICE_HTML }} />
              </>
            )}

            <ArticleEngagement articleId={article.id} />

            <RelatedStories slug={article.slug} limit={3} />

            <div className="mt-12 pt-6 border-t border-zinc-800 flex items-center justify-between">
              <Link to="/stories" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-bold text-sm">
                <ArrowLeft className="h-4 w-4" /> More investigations
              </Link>
              <ShareButtons
                url={shareUrl}
                title={article.title}
                text={article.excerpt || ''}
                testId="article-share-bottom"
              />
            </div>
          </article>
        )}
      </main>

      <SiteFooter />
      <SubmitTipButton />
      {/* Live-reader pill - silent until 2+ readers so tiny numbers don't
          look sad. Uses in-memory heartbeat tracking on the backend. */}
      {article && <LiveReaderPill slug={article.slug} />}
    </div>
  );
};

export default Article;
